import { Order } from '@seen/core/models/orders/Order'
import { VisitNote } from '@seen/core/models/visitNote'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import type { JSX } from 'react'
import { useMemo } from 'react'
import { OrderMention } from '#src/components/OrderMention.tsx'
import { useSearch } from '#src/hooks/index.ts'

interface VisitNoteOrdersWidgetProps {
  visitNote: VisitNote
}

export function VisitNoteOrdersWidget({
  visitNote,
}: VisitNoteOrdersWidgetProps): JSX.Element | null {
  const serviceRequestIdString = useMemo(() => {
    const orderEntries = Object.values(visitNote.sections)
      .flat()
      .filter((s) => VisitNote.isOrderSection(s))
      .flatMap((s) => s.entry ?? [])
    return orderEntries
      .filter((e) => isOfType(e, 'ServiceRequest'))
      .map((r) => resolveId(r))
      .filter(Boolean)
      .join(',')
  }, [visitNote])

  const serviceRequestIds = useMemo(
    () => (serviceRequestIdString ? serviceRequestIdString.split(',') : []),
    [serviceRequestIdString]
  )

  const { data: orders, isLoading: ordersLoading } = useSearch(
    Order,
    serviceRequestIds.length > 0
      ? Order.queryByIds(serviceRequestIds)
      : undefined
  )

  if (serviceRequestIds.length === 0) {
    return null
  }

  const hasOrders = orders && orders.length > 0

  if (!ordersLoading && !hasOrders) {
    return null
  }

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
        <h4 className="text-sm font-medium">Orders</h4>
        {hasOrders && (
          <span className="text-xs text-muted-foreground">{orders.length}</span>
        )}
      </div>
      {ordersLoading ? (
        <div className="px-3 py-4 text-sm text-muted-foreground">
          Loading...
        </div>
      ) : (
        <div className="flex flex-col text-sm">
          {orders?.map((order) => (
            <div
              key={order.id}
              className="px-3 py-1.5 transition-colors hover:bg-accent"
            >
              <OrderMention
                order={order.reference}
                hideParticipant
                className="border-0 bg-transparent px-0 hover:bg-transparent"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
